import { Button } from "@primitives/button";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";

import { isUserAuthenticated } from "@/middleware/auth";
import { removeMemberFromChat } from "@/middleware/members";

type Props = {
  chatId: string;
  open: boolean;
  onOpenClose: (open: boolean) => void;
};

export default function LeaveGroupChat({ chatId, open, onOpenClose }: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { toast } = useToast();

  const handleLeaveGroupChat = async () => {
    const userFingerPrint = isUserAuthenticated();
    if (!chatId || !userFingerPrint?.username) {
      return;
    }
    try {
      setIsLoading(true);
      await removeMemberFromChat({
        chatId,
        username: userFingerPrint.username,
      });
      toast({
        duration: 1000,
        description: t("leftGroupSuccessfully"),
        variant: "default",
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isLoading) {
          return;
        }
        onOpenClose(isOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("leave")}</DialogTitle>
          <DialogDescription>
            {t("areYouSure.toLeaveGroup")}
            <br></br>
            {t("thisActionCannotBeUndone")}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <Button
            disabled={isLoading}
            variant="outline"
            onClick={() => onOpenClose(false)}
          >
            {t("cancel")}
          </Button>

          <Button
            className="min-w-24"
            disabled={isLoading}
            variant="destructive"
            onClick={() => handleLeaveGroupChat()}
          >
            {isLoading ? t("leaving") : t("leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
